import { useEffect, useState } from "react";
import { ArrowUpFromLine } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api, formatApiErrorDetail } from "../../lib/api";
import { usePanelT } from "../../hooks/usePanelT";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function TopUpDialog({ account, open, onOpenChange, walletBalance, onDone }) {
    const { t } = usePanelT();
    const [amount, setAmount] = useState("");
    const [policy, setPolicy] = useState({ fee_by_vertical: {}, fee_percent: 3 });
    const [minTopup, setMinTopup] = useState(50);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setAmount("");
        api.get("/me/ad-policy").then(({ data }) => setPolicy(data)).catch(() => {});
        api.get("/me/ad-accounts/topups/policy").then(({ data }) => setMinTopup(Number(data.min_topup || 50))).catch(() => {});
    }, [open]);

    if (!account) return null;
    const feePercent = Number(policy.fee_by_vertical?.[account.vertical] ?? policy.fee_percent ?? 4);
    const amt = Number(amount) || 0;
    const fee = Math.round(amt * feePercent) / 100;
    const net = Math.round((amt - fee) * 100) / 100;
    const insufficient = amt > walletBalance;
    const valid = amt >= minTopup && !insufficient;
    const name = account.account_name || account.name;

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/me/ad-accounts/${account.id}/topup`, { amount: amt });
            toast.success(t("tu.ok"));
            onDone?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("failed"));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="topup-dialog" className="bg-[#0d0d10] border-white/10 max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl flex items-center gap-2"><ArrowUpFromLine className="w-5 h-5 text-primary" /> {t("tu.title", { name })}</DialogTitle>
                    <DialogDescription>{t("tu.sub")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 flex items-center justify-between">
                        <div>
                            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{t("walletBalance")}</p>
                            <p className="font-display font-bold text-xl" data-testid="topup-wallet-balance">{money(walletBalance)}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{account.platform} · {t(`v.${account.vertical}`) || account.vertical}</p>
                            <p className="text-xs text-muted-foreground">{t("fee")} {feePercent.toFixed(feePercent % 1 ? 2 : 0)}%</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-sm font-medium mb-1">{t("tu.amount")}</p>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input type="number" min={minTopup} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t("tu.min", { n: minTopup })} data-testid="topup-amount" className="bg-background/60 border-white/10 h-11 pl-7" />
                        </div>
                        <div className="flex gap-2 mt-2">
                            {[100, 250, 500, 1000].map((v) => (
                                <button key={v} type="button" onClick={() => setAmount(String(v))} data-testid={`topup-preset-${v}`} className="flex-1 h-8 rounded-lg border border-white/10 text-xs font-mono hover:border-primary/40 hover:text-primary transition-colors">${v}</button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2" data-testid="topup-breakdown">
                        <div className="rounded-xl border border-white/10 bg-background/50 px-3 py-2.5">
                            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("youPay")}</p>
                            <p className="font-display font-bold text-lg tabular-nums" data-testid="topup-pay">{money(amt)}</p>
                        </div>
                        <div className="rounded-xl border border-white/10 bg-background/50 px-3 py-2.5">
                            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("fee")} {feePercent.toFixed(feePercent % 1 ? 2 : 0)}%</p>
                            <p className="font-display font-bold text-lg tabular-nums text-red-300" data-testid="topup-fee">− {money(fee)}</p>
                        </div>
                        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2.5">
                            <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">{t("toAdAccount")}</p>
                            <p className="font-display font-bold text-lg tabular-nums text-emerald-300" data-testid="topup-net">{money(net)}</p>
                        </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{t("tu.note")}</p>
                    <Button onClick={submit} disabled={!valid || busy} data-testid="topup-submit" className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                        {busy ? t("wz.submitting") : insufficient ? t("topUpWalletFirst") : t("tu.submit")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
